import React, { useEffect, useMemo, useState } from "react";
import { Button, Input, message } from "antd";
import { PlusOutlined, SearchOutlined, UserOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { useSelector } from "react-redux";
import UseApi from "../../Reusable/CallApi/UseApi";
import defaultCourseImg from "../../../assets/Icons/course-img.png";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  .title {
    font-size: 20px;
    font-weight: 500;
    color: #1f243b;
  }

  .toolbar {
    display: flex;
    gap: 12px;
  }

  .search-input {
    width: 220px;
    border-radius: 20px;
  }

  .grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 20px;
  }

  .card {
    border: 1px solid #eee;
    border-radius: 10px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .card-cover {
    width: 100%;
    height: 160px;
    object-fit: cover;
    background: #f5f5f5;
  }

  .card-body {
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
  }

  .card-title {
    font-size: 16px;
    font-weight: 500;
    color: #1f243b;
  }

  .card-meta {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #68728a;
    font-size: 13px;
  }

  .card-footer {
    display: flex;
    justify-content: flex-end;
    margin-top: 8px;
    padding-top: 12px;
    border-top: 1px solid #f0f0f0;
  }

  /* Mint green, matching Figma's primary action color - same palette
     already used for Group Files' buttons (Reportpeoplegroup.js). */
  .mint-button {
    background: #c2fae5;
    border-color: #c2fae5;
    color: #1f243b;
  }
  .mint-button:hover,
  .mint-button:focus {
    background: #aef0d4 !important;
    border-color: #aef0d4 !important;
    color: #1f243b !important;
  }
`;

// A real, separate module from My Course (per Figma - two distinct
// sidebar items). There's no separate learner login anywhere in this
// app - see learnerMenuItem's comment in Redux/systemSlice.js.
// viewingAsPeopleId (set via the "Viewing as" picker in the header) is
// who Enroll acts on.
const LearnerCourseList = () => {
  const clientId = useSelector((state) => state.authTracking.userInfo.clientId);
  const viewingAsPeopleId = useSelector((state) => state.systemTracking.viewingAsPeopleId);

  const { data: myCoursesData, request: myCoursesRequest } = UseApi();
  const { data: allCoursesData, loading, request: allCoursesRequest } = UseApi();
  const { request: enrollRequest } = UseApi();

  const [search, setSearch] = useState("");

  const fetchAll = () => {
    if (clientId) {
      allCoursesRequest({ endPoint: `/course/getcourse/${clientId}`, method: "get" }).catch(() => {});
    }
    if (clientId && viewingAsPeopleId) {
      myCoursesRequest({
        endPoint: `/course/getmycourses/${clientId}?people_id=${viewingAsPeopleId}`,
        method: "get",
      }).catch(() => {});
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, viewingAsPeopleId]);

  const myCourses = myCoursesData?.response || [];
  const allCourses = allCoursesData?.response || [];
  const enrolledCourseIds = new Set(myCourses.map((c) => c.course_id));
  // "Hide from Course List" (Course Settings) keeps a course out of the
  // learner catalog. Courses the learner is already enrolled in aren't in
  // this list anyway, so they still see courses they were assigned.
  const availableCourses = allCourses.filter(
    (c) => !enrolledCourseIds.has(c.course_id) && !c.hide_courselist
  );

  const filteredAvailable = useMemo(() => {
    const value = search.toLowerCase().trim();
    if (!value) return availableCourses;
    return availableCourses.filter((c) => (c.course_name || "").toLowerCase().includes(value));
  }, [availableCourses, search]);

  const handleEnroll = async (courseId) => {
    if (!viewingAsPeopleId) {
      message.error("Pick who you're viewing as first");
      return;
    }
    try {
      await enrollRequest({
        endPoint: `/course/enroll/${clientId}`,
        method: "post",
        data: { course_id: courseId, people_id: viewingAsPeopleId },
      });
      message.success("Enrolled successfully");
      fetchAll();
    } catch (error) {
      message.error("Failed to enroll");
    }
  };

  return (
    <Wrapper>
      <div className="title">Course List</div>
      <div className="toolbar">
        <Input
          className="search-input"
          placeholder="Search"
          prefix={<SearchOutlined />}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>
      <div className="grid">
        {filteredAvailable.map((course) => (
          <div className="card" key={course.course_id}>
            <img className="card-cover" src={course.course_profile || defaultCourseImg} alt="" />
            <div className="card-body">
              <div className="card-title">{course.course_name}</div>
              {course.conducted_by && (
                <div className="card-meta">
                  <UserOutlined /> {course.conducted_by}
                </div>
              )}
              <div className="card-footer">
                <Button
                  className="mint-button"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={() => handleEnroll(course.course_id)}
                >
                  Enroll
                </Button>
              </div>
            </div>
          </div>
        ))}
        {!loading && filteredAvailable.length === 0 && <div>No courses available.</div>}
      </div>
    </Wrapper>
  );
};

export default LearnerCourseList;
